import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { AppButton } from "../ui";

/**
 * DashboardEmpty — what a dashboard card says when it has nothing to draw.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * Seven cards on this dashboard used to answer with a shrug: "No data
 * available", "No course data available", "No data available" again. For an
 * institute that has been running a year those strings are fine — the range
 * picker is on "Today" and nothing happened today. For an institute on its
 * first login they are the entire product, and every one of them is a dead end
 * where the next step should be.
 *
 * That is the cheapest onboarding surface in the app: it takes no space that
 * was not already reserved, it appears exactly where the missing thing would
 * be, and it disappears the moment the thing exists. No card, no banner, no
 * checklist to dismiss.
 *
 * ── THE ONE RULE ────────────────────────────────────────────────────────────
 * **The sentence changes with the reason; the action does not.**
 *
 *   structurally empty   "No fees recorded yet."
 *   empty for the range  "No fees collected in this period."
 *
 * Telling a year-old institute to "record your first payment" because they
 * picked Today is the failure mode this guards against — it makes the product
 * look like it has forgotten them. The BUTTON stays either way, because
 * recording a payment is a reasonable thing to do from an empty revenue chart
 * whatever the reason it is empty; only the claim about their history has to be
 * true.
 *
 * Callers pass `isFirstTime` from an institute-level total (`stats.*`), never
 * from the filtered result they are rendering — the filtered result being empty
 * is the thing being explained, so it cannot also be the evidence.
 *
 * ⚠️ A SEPARATE FILE, not a function inside `Dashboard.js`. That file imports
 * `dashboard.scss`, and `lint:tokens --check mixed` fails any file that both
 * carries `tw-` classes and imports a stylesheet — a half-migrated file is a
 * specificity coin-flip decided by bundler order. Split, `Dashboard.js` stays
 * SCSS and this stays Tailwind, and both are whole.
 */
const DashboardEmpty = ({ isFirstTime = false, firstTimeText, periodText, action }) => {
  const navigate = useNavigate();
  const text = (isFirstTime ? firstTimeText : periodText) || periodText || firstTimeText;

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2.5 tw-px-4 tw-py-8 tw-text-center">
      <p className="tw-mb-0 tw-text-[0.8125rem] tw-leading-relaxed tw-text-content-muted">{text}</p>
      {action && (
        <AppButton variant="outline" size="sm" onClick={() => navigate(action.to)}>
          {action.label}
          <ArrowRight size={13} aria-hidden="true" className="tw-ml-1.5" />
        </AppButton>
      )}
    </div>
  );
};

export default DashboardEmpty;
